import {
  Either, Fail, Maybe, Success,
} from 'monet';
import LeftResponse from '../LeftResponse';

/**
 * @description Кабинет.
 * @param {number} id      Идентификатор.
 * @param {string} name    Название.
 * @param {string} comment Комментарий.
 * @param {string} color   Цвет.
 * @constructor
 */
export function Cabinet(id, name, comment, color) {
  this.id = id;
  this.name = name;
  this.comment = comment;
  this.color = color;
}

/**
 * @description Запрос на здание кабинета.
 * @param {string} comment Комментарий.
 * @param {string} name    Название.
 * @constructor
 */
export function CabinetCreatingRequest(name, comment) {
  this.toQuery = () => ({ name, comment });
}

/**
 * @description Запрос на редактирование кабинета.
 * @param {number} id      Идентификатор.
 * @param {string} comment Комментарий.
 * @param {string} name    Название.
 * @constructor
 */
export function CabinetEditingRequest(id, name, comment) {
  this.toQuery = () => ({ name, comment });
  this.getCabinetId = () => id;
}

/**
 * @description Шлюз кабинета.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function CabinetGateway(axios) {
  /**
   * @description Отправить запрос на создание кабинета.
   * @param {CabinetCreatingRequest} request
   * @returns {Promise<Either>}
   */
  this.sendCreateCabinet = (request) => axios
    .post('/api/cabinets', request.toQuery())
    .then(
      async (response) => {
        const cabinetId = response.data.cabinet_id;
        const { message } = response.data;
        return Either.right({ cabinetId, message });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes, interaction, title, detail, whatsAppLink, telegramLink,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left({
            interaction, leftResponse, detail, whatsAppLink, telegramLink,
          });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на редактирование кабинета.
   * @param {CabinetEditingRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.sendEditCabinet = (request) => axios
    .put(`/api/cabinets/${request.getCabinetId()}`, request.toQuery())
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right({ message });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на удаление кабинета.
   * @param {number} id  Идентификатор кабинета.
   * @returns {Promise<Validation>}
   */
  this.sendDeleteCabinet = (id) => axios.delete(`/api/cabinets/${id}`).then(
    async (response) => {
      const cabinetId = id;
      const { message } = response.data;
      return Success({ cabinetId, message });
    },
    async (error) => {
      if (error.isAxiosError && error.response.status === 400) {
        const causes = error.response.data.causes || [];
        const { title } = error.response.data;
        return Fail({ causes, title });
      }
      if (error.isAxiosError && error.response.status === 404) {
        const { title } = error.response.data;
        return Fail({ title });
      }
      throw error; // oh no
    },
  );
  /**
   * @description Получить список кабинетов.
   *
   * @returns {Promise<Maybe>}
   */
  this.getAll = () => axios
    .get('/api/cabinets')
    .then(async (response) => {
      const cabinets = (response.data.data.cabinets || []).map(
        (cabinet) => new Cabinet(
          cabinet.id,
          cabinet.name,
          cabinet.comment,
          cabinet.color,
        ),
      );
      const onBoardingStatus = response.data.data.on_boarding_status;
      return Maybe.fromEmpty({ actions: response.data.actions, cabinets, onBoardingStatus });
    });
  /**
   * @description Получить кабинет по идентификатору.
   * @param {number} cabinetId Идентификатор кабинета.
   * @returns {Promise<Validation>}
   */
  this.getById = (cabinetId) => axios
    .get(`/api/cabinets/${cabinetId}`)
    .then(
      async (response) => {
        const cabinet = new Cabinet(
          response.data.cabinet.id,
          response.data.cabinet.name,
          response.data.cabinet.comment,
          response.data.cabinet.color,
        );
        return Success(cabinet);
      },
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на загрузку изображения кабинета.
   * @param {number} cabinetId
   * @param {FormData} form
   * @returns {Promise<Validation>}
   */
  this.sendUploadCoverCabinet = (cabinetId, form) => axios
    .post(`/api/cabinets/${cabinetId}/cover`, form)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => {
          const failurePairs = (causes || [])
            .map((cause) => [cause.source, cause.details]);
          return Object.fromEntries(failurePairs) || {};
        };
        if (error.isAxiosError && error.response.status === 400) {
          const causes = makeCauses(error.response.data.causes);
          const { title } = error.response.data;
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error; // oh no
      },
    );

  /**
   * @description Отправить запрос на удаление изображения кабинета.
   * @param {number} cabinetId
   * @returns {Promise<Validation>}
   */
  this.sendDeleteCoverCabinet = (cabinetId) => axios
    .delete(`/api/cabinets/${cabinetId}/cover`)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const makeCauses = (causes) => {
          const failurePairs = (causes || [])
            .map((cause) => [cause.source, cause.details]);
          return Object.fromEntries(failurePairs) || {};
        };
        if (error.isAxiosError && error.response.status === 400) {
          const causes = makeCauses(error.response.data.causes);
          const { title } = error.response.data;
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = error.response.data;
          return Fail({ title });
        }
        throw error; // oh no
      },
    );

  /**
   * @description Получить изображение кабинета.
   * @param {number} cabinetId Идентификатор кабинета.
   * @returns {Promise<Validation>}
   */
  this.getCabinetCover = (cabinetId) => axios
    .get(`/api/cabinets/${cabinetId}/cover`, { responseType: 'blob' })
    .then(
      async (response) => Success(response.data),
      async (error) => {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        if (error.isAxiosError && error.response.status === 400) {
          const causes = data.causes || [];
          const { title } = data;
          return Fail({ causes, title });
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { title } = data;
          return Fail({ title });
        }
        throw error; // oh no
      },
    );
}
